import { useUserStore } from 'store';
import { firebaseAuth, getMessagingToken } from 'configs';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from 'firebase/auth';
import {
  checkIdentify,
  signup as signupApi,
  login as loginApi,
  logout as logoutApi,
} from 'api';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '../constants';
import { clearStorage, pushDataLayer, isInvalidLineSessionError } from 'utils';
import { TReqRegister } from 'types';
import ReactGA from 'react-ga4';
import useLine from './useLine';
import useToast from './useToast';

function useAuth() {
  const { setIsAuthenticated, isAuthenticated, subscriptionId, clientId } =
    useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeLineSessionToken,
    lineSessionExpiresAtMs,
    clearLineSession,
    redirectLogin,
  } = useLine();
  const { toastError } = useToast();

  const verifyPhone = async (phone: string, isValid: boolean) => {
    const isIdentify = await checkIdentify(phone);
    if (isIdentify.isValidUser !== isValid || isIdentify.isDisabledUser) {
      throw new Error(isIdentify.message);
    }
  };

  const recaptchaVerifier = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        firebaseAuth,
        'recaptcha-container',
        {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {},
        },
      );
    }
  };

  const sendOTP = async (phone: string) => {
    recaptchaVerifier();

    const appVerifier = window.recaptchaVerifier;

    const confirmationResult = await signInWithPhoneNumber(
      firebaseAuth,
      phone,
      appVerifier,
    );
    window.confirmationResult = confirmationResult;
  };

  const login = async (token: string) => {
    const fcmToken = await getMessagingToken();
    const lineTicket =
      activeLineSessionToken &&
      lineSessionExpiresAtMs != null &&
      new Date(lineSessionExpiresAtMs).getTime() >= Date.now()
        ? activeLineSessionToken
        : undefined;
    try {
      await loginApi(token, fcmToken, subscriptionId, lineTicket);
      setIsAuthenticated(true);
    } catch (error) {
      if (isInvalidLineSessionError(error)) {
        clearLineSession();
        await redirectLogin();
        return;
      }
      toastError(error as Error);
    }
  };

  const logout = async () => {
    signOut(firebaseAuth)
      .then(() => {
        // Sign-out successful.
      })
      .catch((error) => {
        // An error happened.
      });
    const fcmToken = await getMessagingToken();
    await logoutApi(fcmToken, subscriptionId, clientId);
    clearStorage();
    clearLineSession();
    navigate(0);
  };

  const signup = async (req: TReqRegister, token: string) => {
    try {
      const { id } = await signupApi(req, token);
      setIsAuthenticated(true);

      // Send event sign up to GA
      const TRACKING_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
      if (
        TRACKING_ID &&
        id &&
        (process.env.NODE_ENV === 'production' ||
          process.env.REACT_APP_TECO_LOCAL_MODE !== 'true')
      ) {
        if (!ReactGA.isInitialized) {
          ReactGA.initialize(TRACKING_ID);
        }
        ReactGA.event('sign_up', { teco_id: id });
      }
      pushDataLayer({ event: 'sign_up', label: `${id}` });
    } catch (error) {
      if (isInvalidLineSessionError(error)) {
        clearLineSession();
        await redirectLogin();
        return;
      }
      toastError(error as Error);
    }
  };

  const verifyOTP = async (otp: string) => {
    const token = await window.confirmationResult
      .confirm(otp)
      .then(async (res: any) => {
        return res?.user?.stsTokenManager?.accessToken;
      });
    return token;
  };

  const checkNeedToLogin = (redirect = routes.SIGNUP) => {
    if (!isAuthenticated) {
      navigate(redirect, {
        state: { from: { pathname: location.pathname } },
      });
      return true;
    }
    return false;
  };

  return {
    sendOTP,
    logout,
    verifyOTP,
    verifyPhone,
    signup,
    login,
    checkNeedToLogin,
  };
}

export default useAuth;
